<template>
  <div class="pick-calendar-wrapper" ref="wrapperRef">
    <!-- Text input that displays yyyy/mm/dd -->
    <input
      ref="textInputRef"
      type="text"
      :value="displayValue"
      @click="openCalendar"
      @focus="openCalendar"
      :placeholder="placeholder"
      :class="inputClass"
      readonly
    />
    <!-- Hidden date input used for the calendar -->
    <input
      ref="dateInputRef"
      type="date"
      :value="internalValue"
      @change="handleDateChange"
      @blur="handleDateBlur"
      :min="minDate"
      class="date-input-hidden"
    />
    <!-- Calendar icon -->
    <div class="calendar-icon" @click.stop="openCalendar">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2"/>
        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PickCalendar',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    minDate: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'YYYY/MM/DD'
    },
    inputClass: {
      type: String,
      default: 'w-full p-2 md:p-4 border-2 border-black/25 rounded font-archivo text-sm md:text-base bg-white'
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      showCalendar: false
    }
  },
  computed: {
    // Value shown in the text input (yyyy/mm/dd format)
    displayValue() {
      if (!this.modelValue) return ''
      // If it is already in yyyy/mm/dd format, return it as is
      if (this.modelValue.includes('/')) {
        return this.modelValue
      }
      // If it is in yyyy-mm-dd format, convert it to yyyy/mm/dd
      if (this.modelValue.includes('-')) {
        return this.modelValue.replace(/-/g, '/')
      }
      return ''
    },
    // Converts yyyy/mm/dd to yyyy-mm-dd (the date input format)
    internalValue() {
      if (!this.modelValue) return ''
      // If it is already in yyyy-mm-dd format, return it as is
      if (this.modelValue.includes('-')) {
        return this.modelValue
      }
      // If it is in yyyy/mm/dd format, convert it to yyyy-mm-dd
      if (this.modelValue.includes('/')) {
        return this.modelValue.replace(/\//g, '-')
      }
      return ''
    }
  },
  methods: {
    openCalendar() {
      this.showCalendar = true
      // Focuses the date input to open the calendar
      this.$nextTick(() => {
        if (this.$refs.dateInputRef) {
          // Tries to use showPicker() when available (Chrome, Edge)
          if (this.$refs.dateInputRef.showPicker) {
            try {
              this.$refs.dateInputRef.showPicker()
            } catch (error) {
              // Fallback for browsers that do not support showPicker
              this.$refs.dateInputRef.focus()
              this.$refs.dateInputRef.click()
            }
          } else {
            // For browsers that do not support showPicker (Firefox, Safari)
            this.$refs.dateInputRef.focus()
            this.$refs.dateInputRef.click()
          }
        }
      })
    },
    handleDateChange(event) {
      const value = event.target.value
      if (!value) {
        this.$emit('update:modelValue', '')
        this.showCalendar = false
        // Removes the focus from the date input
        if (this.$refs.textInputRef) {
          this.$refs.textInputRef.blur()
        }
        return
      }
      
      // Converts from yyyy-mm-dd (the date input format) to yyyy/mm/dd
      const formattedValue = value.replace(/-/g, '/')
      this.$emit('update:modelValue', formattedValue)
      this.showCalendar = false
      
      // Removes the focus from the date input and returns to the text input
      if (this.$refs.textInputRef) {
        this.$refs.textInputRef.blur()
      }
    },
    handleDateBlur() {
      // Waits a little before hiding the calendar
      setTimeout(() => {
        this.showCalendar = false
      }, 200)
    }
  }
}
</script>

<style scoped>
.pick-calendar-wrapper {
  position: relative;
  width: 100%;
}

.date-input-hidden {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: 2;
}

.date-input-hidden:focus {
  pointer-events: auto;
}

.calendar-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  pointer-events: auto;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.calendar-icon:hover {
  color: rgba(0, 0, 0, 0.7);
}

input[type="text"] {
  padding-right: 40px; /* Space for the icon */
  cursor: pointer;
}
</style>

